/*
 *
 * NewsHomePopups
 *
 * @version 1.0.0
 * @author Patrizia Petrera
 * @since 19-Mar-2025
*/

import React from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import formattedMessage from './languageModule'
import Toggle from 'Hoc/ToggleComponent'
import MerchantModal from '@nexidigital/merchantmodal'
import { parseDeepLink } from 'Utility/deepLinkService'
import PurifyAndReplaceHTML from 'Utility/PurifyAndReplaceHTML'
import { roles } from 'Utility/utils'
import './NewsHomePopups.scss'

const AML_EXPIRY_CATEGORY = 'AML_REMINDER'

const NewsHomePopups = (props) => {
  const navigate = useNavigate()
  const { newsInfo } = props
  const hasActivableTerminals = Boolean(props.hasActivableTerminals)
  const hasQrActivableTerminals = hasActivableTerminals && Number(props.qrActivableTerminals) >= 1
  const isTerminalActivationNews = props.isTerminalActivationVisible && newsInfo.category === 'ACTIVATIONS' && hasActivableTerminals
  const isAmlExpiryNews = newsInfo.category === AML_EXPIRY_CATEGORY
  const canManageAmlReminder = isAmlExpiryNews && [roles.ADMIN, roles.SECONDARY_OFFICER].includes(props.userRole)

  const onClose = () => {
    props.actions.doHideTtl()
    props.actions.doRetrieveTTL()
  }

  const handleAcknowledge = () => {
    props.actions.doHideTtl()
    props.actions.doMarkNewsRead({
      id: newsInfo.id || '',
      retrieveNextTtl: true
    })
  }

  const handleGotoComunicationDetail = () => {
    props.actions.doGetComunicationDetail({ id: newsInfo.id || '' })
  }

  const handleInstallTerminal = () => {
    const { deepLink, otherParams } = parseDeepLink(newsInfo.url)
    props.actions.doMarkNewsRead({
      id: newsInfo.id || '',
      retrieveNextTtl: false
    })
    props.actions.doHideTtl()

    if (deepLink) {
      props.actions.doHandleDeepLinking(deepLink, otherParams)
    }
  }

  const handleGoToCompanyData = () => {
    props.actions.doMarkNewsRead({
      id: newsInfo.id || '',
      retrieveNextTtl: false
    })
    props.actions.doHideTtl()
    navigate('/profile/company-data')
  }

  const newsDate = moment(newsInfo.publishDate).format('DD/MM/YYYY - HH:mm').toUpperCase()

  return (
    <Toggle>
      <MerchantModal
        topBarTitle={newsDate}
        title={newsInfo.title}
        description={PurifyAndReplaceHTML(newsInfo.content) || PurifyAndReplaceHTML(newsInfo.shortContent) || ''}
        onClose={() => onClose()}
        primaryButtonLabel={canManageAmlReminder
          ? formattedMessage.goToCompanyData
          : isAmlExpiryNews
            ? formattedMessage.acknowledge
            : isTerminalActivationNews
              ? (hasQrActivableTerminals ? formattedMessage.installTerminal : formattedMessage.acknowledge)
              : formattedMessage.confirm}
        primaryAction={() => (canManageAmlReminder
          ? handleGoToCompanyData()
          : isAmlExpiryNews
            ? handleAcknowledge()
            : isTerminalActivationNews
              ? (hasQrActivableTerminals ? handleInstallTerminal() : handleAcknowledge())
              : handleGotoComunicationDetail())}
        primaryButtonLoading={props.isLoading}
        secondaryButtonLabel={canManageAmlReminder
          ? formattedMessage.acknowledge
          : isTerminalActivationNews && hasQrActivableTerminals ? formattedMessage.acknowledge : ''}
        secondaryAction={canManageAmlReminder
          ? handleAcknowledge
          : isTerminalActivationNews && hasQrActivableTerminals ? handleAcknowledge : undefined}
      >
        {isAmlExpiryNews && newsInfo.shortContent && (
          <div className='newsHomePopupsShortContent'>
            {PurifyAndReplaceHTML(newsInfo.shortContent)}
          </div>
        )}
      </MerchantModal>
    </Toggle>
  )
}

NewsHomePopups.propTypes = {
  actions: PropTypes.object,
  newsInfo: PropTypes.object,
  isLoading: PropTypes.bool,
  isTerminalActivationVisible: PropTypes.bool,
  userRole: PropTypes.string,
  hasActivableTerminals: PropTypes.bool,
  qrActivableTerminals: PropTypes.number
}

NewsHomePopups.defaultProps = {
  actions: {},
  newsInfo: {},
  isLoading: false,
  isTerminalActivationVisible: false,
  userRole: '',
  hasActivableTerminals: false,
  qrActivableTerminals: 0
}

export default NewsHomePopups
